import { useState, useEffect, useCallback, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ArticleIcon from '@mui/icons-material/Article'
import { desktopLoginChipsService } from '../services'
import useAuthStore from '../store/authStore'

const CHIP_MAX_LENGTH = 80

const CHIP_FIELDS = [
  { id: 'chipProfit', label: 'Profit chip' },
  { id: 'chipOutlets', label: 'Outlets chip' },
  { id: 'chipMargin', label: 'Margin chip' },
]

const emptyChips = () => ({
  chipProfit: { en: '', bn: '' },
  chipOutlets: { en: '', bn: '' },
  chipMargin: { en: '', bn: '' },
})

const errorMessage = (err, fallback) => {
  const msg = err?.response?.data?.message || err?.message || fallback
  return Array.isArray(msg) ? msg.join(', ') : String(msg)
}

const LoginPageContentPage = () => {
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [chips, setChips] = useState(emptyChips)
  const [isCustomized, setIsCustomized] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [confirmReset, setConfirmReset] = useState(false)

  const applyPayload = (data) => {
    const next = emptyChips()
    CHIP_FIELDS.forEach(({ id }) => {
      next[id] = {
        en: data?.chips?.[id]?.en ?? '',
        bn: data?.chips?.[id]?.bn ?? '',
      }
    })
    setChips(next)
    setIsCustomized(!!data?.is_customized)
    setFieldErrors({})
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await desktopLoginChipsService.get()
      applyPayload(data)
      setError(null)
    } catch (e) {
      setError(errorMessage(e, 'Failed to load login page chips'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isSuperuserActor) return
    load()
  }, [load, isSuperuserActor])

  const mismatchWarnings = useMemo(() => {
    return CHIP_FIELDS.filter(({ id }) => {
      const en = String(chips[id]?.en || '').trim()
      const bn = String(chips[id]?.bn || '').trim()
      return (en && !bn) || (!en && bn)
    }).map(({ label }) => label)
  }, [chips])

  const updateField = (id, lang, value) => {
    setChips((prev) => ({
      ...prev,
      [id]: { ...prev[id], [lang]: value },
    }))
    setFieldErrors((prev) => {
      if (!prev[`${id}.${lang}`]) return prev
      const next = { ...prev }
      delete next[`${id}.${lang}`]
      return next
    })
  }

  const validate = () => {
    const nextErrors = {}
    CHIP_FIELDS.forEach(({ id }) => {
      ;['en', 'bn'].forEach((lang) => {
        const value = String(chips[id]?.[lang] ?? '')
        if (value.length > CHIP_MAX_LENGTH) {
          nextErrors[`${id}.${lang}`] = `Max ${CHIP_MAX_LENGTH} characters`
        }
      })
    })
    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    try {
      setSaving(true)
      setError(null)
      const data = await desktopLoginChipsService.update({
        chipProfit: {
          en: chips.chipProfit.en.trim(),
          bn: chips.chipProfit.bn.trim(),
        },
        chipOutlets: {
          en: chips.chipOutlets.en.trim(),
          bn: chips.chipOutlets.bn.trim(),
        },
        chipMargin: {
          en: chips.chipMargin.en.trim(),
          bn: chips.chipMargin.bn.trim(),
        },
      })
      applyPayload(data)
      setSuccessMessage('Login page chips saved')
    } catch (e) {
      setError(errorMessage(e, 'Failed to save login page chips'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      setResetting(true)
      setError(null)
      const data = await desktopLoginChipsService.reset()
      applyPayload(data)
      setConfirmReset(false)
      setSuccessMessage('Login page chips reset to defaults')
    } catch (e) {
      setError(errorMessage(e, 'Failed to reset login page chips'))
    } finally {
      setResetting(false)
    }
  }

  if (!isSuperuserActor) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <ArticleIcon sx={{ color: '#e90000', fontSize: 32 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Login page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit the three promo chips on the desktop web login screen. Leave a
            language blank to hide that chip for that language.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mismatchWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          One language is empty for: {mismatchWarnings.join(', ')}. That chip
          will be hidden in the empty language.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {CHIP_FIELDS.map(({ id, label }) => (
            <Card key={id}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {label}
                </Typography>
                <Stack spacing={2} sx={{ mt: 1, maxWidth: 560 }}>
                  <TextField
                    fullWidth
                    label="English"
                    value={chips[id].en}
                    onChange={(e) => updateField(id, 'en', e.target.value)}
                    error={!!fieldErrors[`${id}.en`]}
                    helperText={
                      fieldErrors[`${id}.en`] ||
                      `${chips[id].en.length}/${CHIP_MAX_LENGTH}`
                    }
                    inputProps={{ maxLength: CHIP_MAX_LENGTH }}
                    disabled={saving || resetting}
                  />
                  <TextField
                    fullWidth
                    label="Bangla"
                    value={chips[id].bn}
                    onChange={(e) => updateField(id, 'bn', e.target.value)}
                    error={!!fieldErrors[`${id}.bn`]}
                    helperText={
                      fieldErrors[`${id}.bn`] ||
                      `${chips[id].bn.length}/${CHIP_MAX_LENGTH}`
                    }
                    inputProps={{ maxLength: CHIP_MAX_LENGTH }}
                    disabled={saving || resetting}
                  />
                </Stack>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Empty text is omitted, matching the live login page.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                English
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 2 }} flexWrap="wrap" useFlexGap>
                {CHIP_FIELDS.map(({ id }) => {
                  const text = chips[id].en.trim()
                  if (!text) return null
                  return <Chip key={`${id}-en`} label={text} size="small" />
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Bangla
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {CHIP_FIELDS.map(({ id }) => {
                  const text = chips[id].bn.trim()
                  if (!text) return null
                  return <Chip key={`${id}-bn`} label={text} size="small" />
                })}
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || resetting}
              sx={{
                bgcolor: '#e90000',
                '&:hover': { bgcolor: '#c40000' },
              }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={resetting ? <CircularProgress size={18} /> : <RestartAltIcon />}
              onClick={() => setConfirmReset(true)}
              disabled={saving || resetting || !isCustomized}
            >
              Reset to defaults
            </Button>
          </Stack>
        </Stack>
      )}

      <Dialog open={confirmReset} onClose={() => !resetting && setConfirmReset(false)}>
        <DialogTitle>Reset login chips?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This restores the original English and Bangla chip copy and discards
            any custom values.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)} disabled={resetting}>
            Cancel
          </Button>
          <Button onClick={handleReset} color="error" disabled={resetting}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  )
}

export default LoginPageContentPage
